import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Modal } from "~/components/Modal";
import { env } from "~/env.mjs";
import { useUser } from "~/modules/user/hooks/useUser";
import { Routes } from "~/routes";
import * as Analytics from "../../analytics";
import { InlineAuthenticationModalContent } from "./InlineAuthenticationModalContent";
import { useInlineAuth } from "../../hooks/useInlineAuth";

// // // //

// Static pages that never count toward (or show) the inline auth paywall
const PAYWALL_EXCLUDED_PAGES: string[] = [
    Routes.home,
    Routes.overview,
    Routes.login,
    Routes.register,
    Routes.authError,
    Routes.authVerify,
    Routes.citations,
    Routes.salesPage,
    // Routes.pricing,
    Routes.payment_success,
    Routes.settingsProfile,
    Routes.settingsPlan,
    Routes.settingsOrganization,
    Routes.newAssetMonitor,
    Routes.assetScreener,
    Routes.blog,
    Routes.search,
    Routes.privacyPolicy,
    Routes.about,
    Routes.termsOfUse,
];

// Dynamic routes excluded from the paywall. `usePathname` returns the concrete
// URL (`/blog/my-post`), never the route pattern (`/blog/[slug]`), so these
// can't be matched by equality against the `Routes` enum.
const PAYWALL_EXCLUDED_PATH_PATTERNS: RegExp[] = [
    /^\/blog\/[^/]+$/, // Routes.blogIndividual
];

/**
 * isPaywallExcludedPath
 * Checks whether a pathname is exempt from the inline authentication paywall
 */
export function isPaywallExcludedPath(pathname: string): boolean {
    // Drop any trailing slash - `/about/` is served as-is (skipTrailingSlashRedirect)
    const normalizedPathname =
        pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

    if (PAYWALL_EXCLUDED_PAGES.includes(normalizedPathname)) {
        return true;
    }

    // Only fall back to the patterns when no static page matched
    return PAYWALL_EXCLUDED_PATH_PATTERNS.some((pattern) =>
        pattern.test(normalizedPathname),
    );
}

interface Props {
    onAuthSuccess?: () => void;
}

function getInternalPageVisitCount(): number {
    if (typeof window === "undefined") return 0;
    const count = sessionStorage.getItem("internal-page-visit-count");
    return count ? parseInt(count, 10) : 0;
}

function incrementInternalPageVisitCount(): number {
    if (typeof window === "undefined") return 0;
    const currentCount = getInternalPageVisitCount();
    const newCount = currentCount + 1;
    sessionStorage.setItem("internal-page-visit-count", newCount.toString());
    return newCount;
}

const MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN =
    env.NEXT_PUBLIC_DEV_MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN ?? 5;

/**
 * InlineAuthenticationModal
 * Handles user authentication via Google or email/OTP verification
 * Self-manages visibility based on user state and feature flags
 */
export function InlineAuthenticationModal({ onAuthSuccess }: Props) {
    const pathname = usePathname();
    const { loading, isLoggedIn } = useUser();
    const isInitialLoad = loading;

    const {
        currentStep,
        isLoading,
        userEmail,
        handleEmailFormSubmit,
        handleGoogleSignIn,
        handleOTPSubmit,
    } = useInlineAuth({ onAuthSuccess });

    const [show, setShow] = useState(false);
    const [internalPageVisitCount, setInternalPageVisitCount] = useState(0);

    // Initialize page visit count from sessionStorage
    useEffect(() => {
        setInternalPageVisitCount(getInternalPageVisitCount());
    }, []);

    // Track internal page visits for authenticated users
    useEffect(() => {
        // NOTE - pathname is null until the Pages Router has resolved a dynamic
        // route; wait for it so the visit isn't counted twice
        if (isLoggedIn || isInitialLoad || pathname === null) {
            return;
        }

        if (!isPaywallExcludedPath(pathname)) {
            const newCount = incrementInternalPageVisitCount();
            setInternalPageVisitCount(newCount);
        }
    }, [isLoggedIn, pathname, isInitialLoad]);

    // Determine if modal should show
    useEffect(() => {
        if (isInitialLoad || isLoggedIn) {
            setShow(false);
            return;
        }

        if (pathname === null) {
            return;
        }

        if (!isPaywallExcludedPath(pathname)) {
            const showModal = () => {
                Analytics.trackInlineAuthenticationModalOpen();
                setShow(true);
            };

            const showModalWithDelay = () => {
                // 5-second delay
                const delayTimer = setTimeout(showModal, 5000);
                return () => clearTimeout(delayTimer);
            };

            // Show modal after 5th visit
            if (
                internalPageVisitCount >= MAX_INTERNAL_PAGE_VISITS_WITHOUT_LOGIN
            ) {
                return showModalWithDelay();
            }
        } else {
            setShow(false);
        }
    }, [isLoggedIn, pathname, isInitialLoad, internalPageVisitCount]);

    return (
        <Modal
            show={show}
            allowClose={false}
            size="md"
            allowScroll={false}
            blurBackground={true}
            viewport="inset"
        >
            <InlineAuthenticationModalContent
                currentStep={currentStep}
                isLoading={isLoading}
                userEmail={userEmail}
                onGoogleSignIn={handleGoogleSignIn}
                onEmailSubmit={handleEmailFormSubmit}
                onOTPSubmit={handleOTPSubmit}
            />
        </Modal>
    );
}
